import type { QrSticker, StickerTheme } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chargersKey, qrStickersKey } from '../../api/chargers';
import { del, downloadFile, get, post } from '../../api/client';

export { qrStickersKey };

/** Every sticker still in use, newest first (admins). */
export const useQrStickers = (enabled = true) => useQuery({ queryKey: qrStickersKey, queryFn: () => get<QrSticker[]>('/qr-stickers'), enabled });

/** Stickers change with chargers (a socket given one), and chargers with stickers (one deleted). */
function useStickerMutation<A, R>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: qrStickersKey }), qc.invalidateQueries({ queryKey: chargersKey })]),
  });
}

export const useMakeStickers = () => useStickerMutation((count: number) => post<{ batch: number; ids: string[] }>('/qr-stickers/batch', { count }));
export const useDeleteSticker = () => useStickerMutation((id: string) => del(`/qr-stickers/${id}`));
/** Saves the PDF (one sticker) or a zip of PDFs; resolves with the file name. */
export const useDownloadStickers = () => useStickerMutation(({ ids, theme }: { ids: string[]; theme: StickerTheme }) => downloadFile('/qr-stickers/download', { ids, theme }));

/** "Socket A" on chargers with more than one. */
export const socketLetter = (connectorId: number) => `Socket ${String.fromCharCode(64 + connectorId)}`;
/** Where a sticker is: "HD2 · Socket A". */
export const stickerPlace = (c: NonNullable<QrSticker['charger']>) => (c.connectorCount > 1 ? `${c.name} · ${socketLetter(c.connectorId)}` : c.name);
/** An Excel list of the stickers, with their links. Downloads; changes nothing. */
export const exportStickers = (ids: string[]) => downloadFile('/qr-stickers/export', { ids });
/** Where a sticker's charger is: "Svitzer", or "Svitzer (Dockyard)" when the organisation has several sites. */
export const stickerSite = (c: NonNullable<QrSticker['charger']>) => (c.organisationSiteCount > 1 ? `${c.organisationName} (${c.siteName})` : c.organisationName);
export const useDeleteStickers = () => useStickerMutation((ids: string[]) => post<{ deleted: number }>('/qr-stickers/delete', { ids }));
